import { pathLabelOf, type Container, type Item, type SearchResult } from "@egalregal/shared";
import { getMirroredItems, getMirroredPlacements } from "../search/catalogStore";
import { toSearchHits, type SearchHit } from "../search/searchHits";
import type { PlacementOption } from "./placements";

/**
 * Project a resolved scan target into the search result view model, so the
 * scanner preview is literally search level 2 (`HitLevelTwo`) rather than a
 * lookalike (scanner-surface 05 / ticket 07).
 *
 * The catalog and item mirror are the same sources search uses; a resolved
 * container that is not in the loaded slice (shared entry) is appended so its
 * path still resolves.
 *
 * @param container the resolved container
 * @param containers the readable union the path is derived from
 * @returns the search hit that level 2 renders
 */
export function hitForContainer(
  container: Container,
  containers: readonly Container[],
): SearchHit {
  const pool = containers.some((entry) => entry.id === container.id)
    ? containers
    : [...containers, container];
  const result: SearchResult = {
    type: "container",
    container,
    path: pathLabelOf(pool, container.id),
    score: 1,
  };
  return toSearchHits([result], pool, getMirroredItems(), getMirroredPlacements())[0]!;
}

/**
 * Project a known item plus its resolved placements. The first placement (the
 * placement list is sorted by path) is the row's primary host; every placement
 * stays in the hit so level 2 can link each one.
 *
 * @param item the resolved item
 * @param placements its resolved placements
 * @param containers the readable union the placement paths are derived from
 * @returns the search hit that level 2 renders
 */
export function hitForItem(
  item: Item,
  placements: readonly PlacementOption[],
  containers: readonly Container[],
): SearchHit {
  const primary = placements[0];
  const mirrored = getMirroredItems();
  const items = mirrored.some((entry) => entry.id === item.id) ? mirrored : [...mirrored, item];
  const result: SearchResult = {
    type: "item",
    item,
    containerId: primary?.containerId ?? "",
    containerName: primary?.name ?? "",
    path: primary?.path ?? "",
    score: 1,
  };
  return toSearchHits([result], containers, items, getMirroredPlacements())[0]!;
}
